/**
 * SaveStatusIndicator — Live-Anzeige des Save-Status für den Mandanten.
 *
 * Sitzt oben rechts im Mandanten-Wizard-Layout. Drei Zustände:
 *   - saving: Spinner + „Eingaben werden gesichert"
 *   - saved (mit Zeitstempel): Häkchen + „Daten gesichert · vor X Sek."
 *     (Zeitstempel refreshed sich alle 30 s, damit „vor 5 Sek." nach einer
 *     Minute zu „vor 1 Min." wird)
 *   - error: rotes Symbol + Fehler-Text + Retry-Button
 *
 * Für Berater wird der Indikator nicht angezeigt — die kennen das System
 * bereits, und im Cockpit ist Bildschirmfläche kostbar.
 *
 * A11y:
 *   - role="status" + aria-live="polite" — Screen-Reader bekommen die
 *     Statuswechsel mit, ohne aggressiv unterbrochen zu werden
 *   - Retry-Button hat min-height 44px (Touch-Target)
 *
 * Quelle der Wahrheit: der Hook bekommt status + lastSavedAtIso + onRetry
 * von oben (z.B. via useWizardSync.flush() für die Retry-Mechanik). Bewusst
 * KEIN direkter Zugriff auf den Store hier — das macht die Komponente in
 * Tests trivial mockbar.
 */

import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, RotateCcw, Loader2 } from 'lucide-react';
import { de } from '../../lib/i18n/de';

export type SaveStatusKind = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusIndicatorProps {
  /** Welcher der vier Zustände ist gerade aktiv? */
  readonly status: SaveStatusKind;
  /**
   * ISO-Zeitstempel des letzten erfolgreichen Saves. Wird für die relative
   * Zeitangabe verwendet. `null` zeigt im idle-Zustand „Auto-Save aktiv" statt
   * eines Zeitstempels.
   */
  readonly lastSavedAtIso?: string | null;
  /** Callback für den Retry-Button (nur im error-State sichtbar). */
  readonly onRetry?: () => void;
  /** Optionale Klasse für äußeres Layout-Tuning. */
  readonly className?: string;
}

export function SaveStatusIndicator({
  status,
  lastSavedAtIso,
  onRetry,
  className,
}: SaveStatusIndicatorProps) {
  // „now" alle 30 s tickern lassen, damit die relative Zeit aktualisiert
  // wird, ohne dass der Parent neu rendert.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      clearInterval(handle);
    };
  }, []);

  const wrapperBase =
    'inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest transition-colors';

  if (status === 'saving') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`${wrapperBase} border-stone-200 bg-white text-stone-500 ${className ?? ''}`}
      >
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        <span>{de.mandant.saveStatus.saving}</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={`${wrapperBase} flex-wrap gap-x-3 border-rose-200 bg-rose-50 text-rose-800 ${className ?? ''}`}
      >
        <AlertCircle size={12} aria-hidden="true" />
        <span>{de.mandant.saveStatus.error}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex min-h-[28px] items-center gap-1 rounded-sm border border-rose-300 bg-white px-2 py-0.5 font-sans text-[11px] font-medium uppercase tracking-widest text-rose-800 hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-700 focus-visible:ring-offset-2"
          >
            <RotateCcw size={10} aria-hidden="true" />
            {de.mandant.saveStatus.retry}
          </button>
        )}
      </div>
    );
  }

  if (status === 'saved' || lastSavedAtIso) {
    const label = lastSavedAtIso
      ? formatRelativeSave(lastSavedAtIso, now)
      : de.mandant.saveStatus.idle;
    return (
      <div
        role="status"
        aria-live="polite"
        className={`${wrapperBase} border-stone-200 bg-white text-stone-500 ${className ?? ''}`}
      >
        <CheckCircle2 size={12} className="text-emerald-600" aria-hidden="true" />
        <span>{label}</span>
      </div>
    );
  }

  // idle ohne Zeitstempel — dezenter Hinweis, dass Auto-Save aktiv ist.
  // text-stone-500 statt -300 für WCAG-AA-Kontrast (≥ 4.5:1 auf weiß).
  return (
    <div
      role="status"
      aria-live="polite"
      className={`${wrapperBase} border-transparent bg-transparent text-stone-500 ${className ?? ''}`}
    >
      <span>{de.mandant.saveStatus.idle}</span>
    </div>
  );
}

/**
 * Formatiert einen ISO-Zeitstempel in eine deutsche, ohrenfreundliche
 * Relativangabe.
 *
 * Schwellen (orientiert an Apple HIG):
 *   < 10 s         → „gerade eben"
 *   10–59 s        → „vor X Sekunden"
 *   60 s – 59 min  → „vor X Min."
 *   1 h – 23:59 h  → „vor X Std."
 *   gestern        → „gestern"
 *   älter          → Datum dd.mm.
 */
export function formatRelativeSave(savedAtIso: string, nowMs: number): string {
  const savedMs = Date.parse(savedAtIso);
  if (Number.isNaN(savedMs)) return de.mandant.saveStatus.idle;
  const deltaSec = Math.max(0, Math.round((nowMs - savedMs) / 1000));

  if (deltaSec < 10) return de.mandant.saveStatus.savedJustNow;
  if (deltaSec < 60) {
    return de.mandant.saveStatus.savedSecondsAgo.replace('{seconds}', deltaSec.toString());
  }
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) {
    return de.mandant.saveStatus.savedMinutesAgo.replace('{minutes}', deltaMin.toString());
  }
  const deltaH = Math.floor(deltaMin / 60);
  if (deltaH < 24) {
    return de.mandant.saveStatus.savedHoursAgo.replace('{hours}', deltaH.toString());
  }
  if (deltaH < 48) return de.mandant.saveStatus.savedYesterday;

  // Älter als „gestern" → kompaktes deutsches Datum
  const d = new Date(savedMs);
  const dd = d.getDate().toString().padStart(2, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  return `Daten gesichert · ${dd}.${mm}.`;
}
